import type { AdminCompletionStateFilter, AdminCompletionWindow } from "~vue/api/generated/reflections-admin";

export const COMPLETION_WINDOWS: readonly AdminCompletionWindow[] = ["active", "last_30_days", "last_quarter", "last_year", "all_time"];
export const COMPLETION_STATES: readonly AdminCompletionStateFilter[] = ["all", "submitted", "in_progress", "not_started"];
export const DEFAULT_WINDOW: AdminCompletionWindow = "active";
export const DEFAULT_STATE: AdminCompletionStateFilter = "all";

const WINDOW_PARAM = "window";
const STATE_PARAM = "state";

export interface AdminHubQuery {
    window: AdminCompletionWindow;
    state: AdminCompletionStateFilter;
}

export function isCompletionWindow(value: unknown): value is AdminCompletionWindow {
    return typeof value === "string" && (COMPLETION_WINDOWS as readonly string[]).includes(value);
}

export function isCompletionState(value: unknown): value is AdminCompletionStateFilter {
    return typeof value === "string" && (COMPLETION_STATES as readonly string[]).includes(value);
}

// An unknown or missing value falls back to the default, so a stale link still opens the hub.
export function readAdminHubQuery(search: string = globalThis.location?.search ?? ""): AdminHubQuery {
    const params = new URLSearchParams(search);
    const windowValue = params.get(WINDOW_PARAM);
    const stateValue = params.get(STATE_PARAM);
    return {
        window: isCompletionWindow(windowValue) ? windowValue : DEFAULT_WINDOW,
        state: isCompletionState(stateValue) ? stateValue : DEFAULT_STATE,
    };
}

// Defaults are left out so the plain hub link stays the canonical one. The
// history entry is kept as is: ReflectionsHub stores its own state there.
export function writeAdminHubQuery(query: AdminHubQuery): void {
    const url = new URL(globalThis.location.href);
    setOrDelete(url.searchParams, WINDOW_PARAM, query.window === DEFAULT_WINDOW ? null : query.window);
    setOrDelete(url.searchParams, STATE_PARAM, query.state === DEFAULT_STATE ? null : query.state);
    if (url.href !== globalThis.location.href) {
        globalThis.history.replaceState(globalThis.history.state, "", url);
    }
}

export function stripAdminHubQuery(params: URLSearchParams): void {
    params.delete(WINDOW_PARAM);
    params.delete(STATE_PARAM);
}

function setOrDelete(params: URLSearchParams, name: string, value: string | null): void {
    if (value === null) {
        params.delete(name);
    } else {
        params.set(name, value);
    }
}
